import { find, get, some } from 'lodash';
import { BACS_CREDIT_CODE, BACS_DEBIT_CODE, TYPE_IFT, TYPE_IFT_PAYEE } from '../../config/';
import { formatAmount, getAmountWithCurrency } from '../../helpers/fieldHelpers';
import { getPayeeType } from '../../managePayee/states/selectors';
import { CAPABILITIES } from '../../utils/accountUtils';
import { getPayeeById, isBiller } from '../commonSelectors/selectors';
import { getIsLCV } from '../modes/selectors';
import { getSelectedFromAccountDetails } from '../payeeSelector/selectors';

const getBalanceWithCurrency = (amount) => amount && `£${amount}`;

const getBalances = (balances, TYPE_IFT = '') => {
	const current = find(balances, { type: 'current' });
	const available = find(balances, { type: 'available' });

	const currentAmount = get(current, 'amount', '');
	const currentAmountIsObject = typeof currentAmount === 'object';
	const currentAmountValue = currentAmountIsObject ? get(currentAmount, 'value', '') : currentAmount;

	const availableAmount = get(available, 'amount', '');
	const availableAmountIsObject = typeof availableAmount === 'object';
	const availableAmountValue = availableAmountIsObject ? get(availableAmount, 'value', '') : availableAmount;

	if (TYPE_IFT === 'IFT') {
		const availableCurrency = availableAmountIsObject
			? get(availableAmount, 'currency', '')
			: get(available, 'currency', '');
		const currentCurrency = currentAmountIsObject ? get(currentAmount, 'currency', '') : get(current, 'currency', '');

		return {
			currentBalance: getAmountWithCurrency(currentCurrency, currentAmountValue),
			availableBalance: getAmountWithCurrency(availableCurrency, availableAmountValue),
		};
	}
	return {
		currentBalance: getBalanceWithCurrency(formatAmount(currentAmountValue)),
		availableBalance: getBalanceWithCurrency(formatAmount(availableAmountValue)),
	};
};

export const getFormatedAccounts = (accounts, TYPE_IFT) =>
	accounts.map((account) => {
		const { actionsAvailable } = account;
		return {
			id: account.id,
			sortCode: account.sortCode,
			accountNumber: account.accountNumber,
			number: account.number,
			type: account.type,
			displayName: account.displayName,
			accountHolder: get(account, 'metadata.account_name', ''),
			...getBalances(account.balances, TYPE_IFT),
			actionsAvailable: {
				'/account/payments/transfer/out': actionsAvailable['/account/payments/transfer/out'],
				'/account/payments/uk/default/out': actionsAvailable['/account/payments/uk/default/out'],
				'/account/beneficiaries/read': actionsAvailable['/account/beneficiaries/read'],
				'/account/payments/transfer/in': actionsAvailable['/account/payments/transfer/in'],
				'/account/mandates/so/write': actionsAvailable['/account/mandates/so/write'],
				'/account/payments/bacscredit/out': actionsAvailable['/account/payments/bacscredit/out'],
				'/account/payments/bacsdebit/out': actionsAvailable['/account/payments/bacsdebit/out'],
				[CAPABILITIES.IFT]: actionsAvailable[CAPABILITIES.IFT],
				'/account/beneficiaries/account/readwrite': actionsAvailable['/account/beneficiaries/account/readwrite'],
			},
		};
	});

export const filterAccountsForAddPayee = (account, state) => {
	if (getPayeeType(state) === TYPE_IFT_PAYEE) {
		return account.actionsAvailable['/account/payments/ift/out'];
	}
	return account.actionsAvailable['/account/beneficiaries/account/readwrite'];
};

export const getAllAccounts = (state) => state.accounts;

export const getAccountById = (state, id) => state.accounts.find((account) => account.id === id);

export const getAccountBySortCodeAccountNumber = (state, sortCode = '', accountNumber = '') =>
	find(getAllAccounts(state), { sortCode, accountNumber });

export const getFromAccounts = (state, accountsFilter) => {
	const accounts = accountsFilter
		? state.accounts.filter(accountsFilter)
		: state.accounts.filter(
				(account) =>
					account.actionsAvailable['/account/payments/transfer/out'] ||
					account.actionsAvailable['/account/payments/uk/default/out']
		  );

	const fromAccountInState = getSelectedFromAccountDetails(state);
	if (fromAccountInState && accounts.every((account) => account.id !== fromAccountInState.id)) {
		accounts.push(fromAccountInState);
	}
	return getFormatedAccounts(accounts);
};

export const isAddPayeeEnable = (state, fromAccountId) => {
	if (fromAccountId) {
		return filterAccountsForAddPayee(getAccountById(state, fromAccountId), state);
	}
	return state.accounts.some((account) => filterAccountsForAddPayee(account, state));
};

export const getAccountsForAddPayee = (state) => {
	const payeeType = getPayeeType(state) === TYPE_IFT_PAYEE ? 'IFT' : '';
	return getFormatedAccounts(
		state.accounts.filter((account) => filterAccountsForAddPayee(account, state)),
		payeeType
	);
};

export const getToAccounts = (state, toAccountFilter) => {
	const accounts = toAccountFilter
		? state.accounts.filter(toAccountFilter)
		: state.accounts.filter((account) => account.actionsAvailable['/account/payments/transfer/in']);
	return getFormatedAccounts(accounts);
};

export const canPayBeneficiaries = (state, id) => {
	const account = getAccountById(state, id);
	const result = account && account.actionsAvailable['/account/beneficiaries/read'] === true;
	return result;
};

export const getToAccountById = (state, id) => {
	let account = getAccountById(state, id);
	if (account) {
		return {
			displayName: account.displayName,
			accountNumber: account.accountNumber,
			sortCode: account.sortCode,
			iban: account.iban,
			type: account.type,
		};
	}

	account = getPayeeById(state, id);

	return {
		displayName: get(account, 'display_name', '') || get(account, 'to_account.name', ''),
		accountNumber: get(account, 'to_account.account_number', ''),
		sortCode: get(account, 'to_account.sort_code', ''),
		iban: get(account, 'to_account.iban', ''),
		type: 'payee',
	};
};

export const getFromAccountById = (state, id) => {
	const account = getAccountById(state, id) || {};
	const { displayName = '', metadata = {}, accountNumber = '', sortCode = '', type = '', number = '' } = account;
	return { displayName, metadata, accountNumber, sortCode, type, number };
};

export const isCreditCard = (state, id) => {
	const account = getAccountById(state, id);
	return !!account && account.type === 'credit_card';
};

export const isSavingsAccount = (state, id) => {
	const account = getAccountById(state, id);
	return !!account && account.type === 'savings';
};

export const getAccountIsBillerOrCreditCard = (state, id) => isCreditCard(state, id) || isBiller(state, id);

export const isFutureDatedPaymentUnsupported = (state, fromAccount, toAccount) =>
	isSavingsAccount(state, fromAccount) || isBiller(state, toAccount) || isCreditCard(state, fromAccount);

export const getBacsIdsByAccountIdAndBacsType = (state, accountId, bacsType) => {
	const { bacs } = getAccountById(state, accountId) || getSelectedFromAccountDetails(state) || { bacs: [] };
	return bacs.filter((bacsInfo) => bacsInfo.type === bacsType).map((bascInfo) => bascInfo.id);
};
export const getAccountsForBacsOnType = (state, bacsType) => {
	const capabilty =
		bacsType === BACS_CREDIT_CODE ? '/account/payments/bacscredit/out' : '/account/payments/bacsdebit/out';

	const accounts = state.accounts.filter(
		(account) => account.actionsAvailable[capabilty] && 'bacs' in account && some(account.bacs, { type: bacsType })
	);

	const fromAccountInState = getSelectedFromAccountDetails(state);

	if (fromAccountInState && accounts.every((account) => account.id !== fromAccountInState.id)) {
		accounts.push(fromAccountInState);
	}
	return getFormatedAccounts(accounts);
};

export const getAccountsWithIFTCapability = (state) =>
	getFormatedAccounts(
		state.accounts.filter((account) => account.actionsAvailable[CAPABILITIES.IFT]),
		TYPE_IFT
	);

export const getAccountsWithChapsCapability = (state) =>
	getFormatedAccounts(state.accounts.filter((account) => account.actionsAvailable[CAPABILITIES.CHAPS]));

export const getInternalAccountsForBacs = (state, accountId) =>
	getFormatedAccounts(
		state.accounts.filter(
			(account) =>
				(account.actionsAvailable[CAPABILITIES.BACS] || account.actionsAvailable[CAPABILITIES.BACSDEBIT]) &&
				account.id !== accountId
		)
	);

export const canDoBacsPayment = (state, bacsType) => !!getAccountsForBacsOnType(state, bacsType).length;

export const canDoIFTPayment = (state) =>
	!!state.accounts.find((account) => account.actionsAvailable[CAPABILITIES.IFT]);

export const canDoChapsPayment = (state) =>
	!!state.accounts.find((account) => account.actionsAvailable[CAPABILITIES.CHAPS]);

export const canShowPaymentTabs = (state) => {
	if (getIsLCV(state)) return true;
	if (canDoIFTPayment(state)) return true;
	if (canDoChapsPayment(state)) return true;
	if (canDoBacsPayment(state, BACS_CREDIT_CODE)) return true;
	if (canDoBacsPayment(state, BACS_DEBIT_CODE)) return true;
	return false;
};

export const getSelectedAccount = (state, payees, accountId, selectedAccount) => {
	const selectedAccountFromAccountId = find(state, (account) => account.id === accountId);
	if (selectedAccountFromAccountId) {
		return {
			displayName: selectedAccountFromAccountId.displayName,
			sortCode: selectedAccountFromAccountId.sortCode,
			type: selectedAccountFromAccountId.type,
			number: selectedAccountFromAccountId.number,
			accountNumber: selectedAccountFromAccountId.accountNumber,
			accountHolder: selectedAccountFromAccountId.accountHolder,
		};
	}

	const selectedAccountFromPayee = find(payees, (payees) => payees.id === accountId);

	if (selectedAccountFromPayee) {
		return {
			displayName: selectedAccountFromPayee.display_name || selectedAccountFromPayee.displayName,
			sortCode: selectedAccountFromPayee.sortCode,
			accountNumber: selectedAccountFromPayee.accountNumber,
			iban: selectedAccountFromPayee.iban,
			bic: get(selectedAccountFromPayee, 'to_account.bic', ''),
		};
	}
	if (selectedAccount) {
		return {
			displayName: selectedAccount.name || selectedAccount.display_name,
			sortCode: selectedAccount.sort_code,
			accountNumber: selectedAccount.account_number,
		};
	}

	return null;
};
export const getAccountCurrency = (state, fromAccountId) => {
	const balances = get(
		state.accounts.find((account) => account.id === fromAccountId),
		'balances',
		[]
	);
	const currency = get(find(balances, { type: 'current' }), 'amount.currency', '');
	if (currency === '') {
		return get(
			state.accounts.find((account) => account.id === fromAccountId),
			'currency',
			''
		);
	}
	return currency;
};

export const isKapitiAccount = (state, id) => {
	const account = getAccountById(state, id);
	return !!account && account.type === 'CRCURR';
};

export const getPaymentWithAccount = (state, payment) => ({
	...payment,
	fromAccountDetails: getFromAccountById(state, payment.fromAccount),
	toAccountDetails: getToAccountById(state, payment.toAccount),
});

export const getPaymentsWithAccounts = (state, payments) =>
	payments.map((payment) => getPaymentWithAccount(state, payment));
